import type { PropsWithChildren, ReactNode } from 'react'

import { FieldStyled } from './styles/FieldStyled'
import { LabelBadgeStyled } from './styles/LabelBadgeStyled'
import { FieldHelperText } from '../../elements/FieldHelperText'
import { InputLabel } from '../Form/InputLabel'
import {
  InfoBadge,
  type InfoBadgeType,
  InfoBadgeTypes,
} from '@ui/elements/Badge'
import { Icon, type IconTypes } from '@ui/elements/Icon'
import { WithTooltip } from '@ui/elements/Tooltip'

export interface IFieldLabelBadge {
  text: string
  type?: InfoBadgeType
}

export interface IFieldProps {
  layout?: 'horizontal' | 'vertical'
  label?: string | ReactNode
  labelId?: string
  labelFor?: string
  labelTooltipMessage?: string | ReactNode
  labelTooltipAriaLabel?: string
  labelIcon?: IconTypes
  labelBadge?: IFieldLabelBadge
  helperText?: string
  error?: boolean
  required?: boolean
}

export const Field = ({
  layout = 'vertical',
  label,
  labelId,
  labelFor,
  labelTooltipMessage,
  labelIcon,
  labelBadge,
  labelTooltipAriaLabel,
  helperText,
  error,
  required,
  children,
  ...otherProps
}: PropsWithChildren<IFieldProps>) => {
  return (
    <FieldStyled layout={layout} {...otherProps}>
      {label && (
        <InputLabel
          id={labelId}
          htmlFor={labelFor}
          error={error}
          // bypass MUI's behavior of appending its asterisk after the badge
          required={required && !labelBadge}
        >
          {label}

          {labelIcon && (
            <WithTooltip
              title={labelTooltipMessage}
              placement='top'
              isDisabled={!labelTooltipMessage}
            >
              <Icon
                icon={labelIcon}
                ml={0.25}
                ariaLabel={labelTooltipAriaLabel}
              />
            </WithTooltip>
          )}

          {labelBadge && (
            <>
              {required && (
                <span className='formLabelAsterisk' aria-hidden>
                  {' *'}
                </span>
              )}
              <LabelBadgeStyled>
                <InfoBadge
                  type={labelBadge.type ?? InfoBadgeTypes.LOW_PRIORITY}
                  text={labelBadge.text}
                  size='small'
                  fontSize={12}
                />
              </LabelBadgeStyled>
            </>
          )}
        </InputLabel>
      )}

      <div>
        {children}

        {helperText && (
          <FieldHelperText error={error}>{helperText}</FieldHelperText>
        )}
      </div>
    </FieldStyled>
  )
}
